import { useTooltip } from '@/hooks/useTooltip'
import { createPortal } from 'react-dom'
import { useReactFlow } from 'reactflow'

interface FlowchartControlsProps {
  onReset: () => void
}

function TooltipButton({
  onClick,
  title,
  children,
}: {
  onClick: () => void
  title: string
  children: React.ReactNode
}) {
  const { showTooltip, tooltipPosition, buttonRef, showTooltipWithDelay, hideTooltip } = useTooltip(
    { position: 'right' }
  )

  return (
    <>
      <button
        ref={buttonRef}
        onClick={onClick}
        onMouseEnter={showTooltipWithDelay}
        onMouseLeave={hideTooltip}
        className="flex h-8 w-8 items-center justify-center rounded text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800"
      >
        {children}
      </button>
      {showTooltip &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="font-hanken-grotesk pointer-events-none fixed z-[9999] -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            {title}
          </div>,
          document.body
        )}
    </>
  )
}

export function FlowchartControls({ onReset }: FlowchartControlsProps) {
  const { zoomIn, zoomOut } = useReactFlow()

  const handleZoomIn = () => {
    zoomIn({ duration: 200 })
  }

  const handleZoomOut = () => {
    zoomOut({ duration: 200 })
  }

  const handleReset = () => {
    onReset()
  }

  return (
    <div className="absolute left-4 top-4 z-10 flex flex-col gap-2">
      <div className="rounded-lg border border-gray-200 bg-white/90 p-1 shadow-lg backdrop-blur-sm">
        <TooltipButton onClick={handleZoomIn} title="Zoom in">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </TooltipButton>
        <TooltipButton onClick={handleZoomOut} title="Zoom out">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </TooltipButton>
        <TooltipButton onClick={handleReset} title="Reset view">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
            <path d="M21 3v5h-5"></path>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
            <path d="M3 21v-5h5"></path>
          </svg>
        </TooltipButton>
      </div>
    </div>
  )
}
